import { useEffect, useState, useCallback } from "react";
import { Download, X, Share, Plus, Smartphone } from "lucide-react";

/**
 * PWA-Install-Widget
 *
 * Chrome/Edge/Android:
 *   Fängt `beforeinstallprompt` ab und zeigt einen kleinen „App installieren"-
 *   Button im Header (Desktop) bzw. Mobile-Menü. Klick → nativer Prompt.
 *
 * iOS (Safari):
 *   Safari sendet kein `beforeinstallprompt`. Statt dessen zeigen wir beim
 *   ersten Besuch einen dezenten Bottom-Toast mit der 3-Schritt-Anleitung
 *   (Teilen-Icon → „Zum Home-Bildschirm"). Der Toast merkt sich per
 *   localStorage, dass der Nutzer ihn gesehen hat.
 *
 * Beide Widgets erkennen automatisch, wenn die App bereits als PWA läuft
 * (`display-mode: standalone`) und verstecken sich dann komplett.
 */

const STORAGE_KEYS = {
  IOS_HINT_SEEN: "dh-pwa-ios-hint-seen-v1",
  INSTALL_DISMISSED: "dh-pwa-install-dismissed-v1",
};

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari standalone flag
    window.navigator.standalone === true;
}

function isIOSSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  return isIOS && isSafari;
}

/**
 * Kompakte Header-Version (Icon + Label). Zeigt nur, wenn Prompt verfügbar.
 * Auf iOS Safari zeigt sie einen Info-Trigger, der das Modal aufmacht.
 */
export function PwaInstallHeaderButton({ className = "", showLabel = true, "data-testid": testId = "pwa-install-btn" }) {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [iosModalOpen, setIosModalOpen] = useState(false);
  const isIOS = typeof window !== "undefined" && isIOSSafari();

  useEffect(() => {
    if (installed) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [installed]);

  const handleClick = useCallback(async () => {
    if (isIOS) {
      setIosModalOpen(true);
      return;
    }
    if (!prompt) return;
    prompt.prompt();
    const choice = await prompt.userChoice.catch(() => null);
    if (choice?.outcome === "accepted") setInstalled(true);
    setPrompt(null);
  }, [prompt, isIOS]);

  // Weder Prompt noch iOS → gar nichts anzeigen (App ist entweder schon
  // installiert oder Browser unterstützt PWA nicht sichtbar).
  if (installed) return null;
  if (!prompt && !isIOS) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid={testId}
        title="Als App aufs Handy installieren"
        className={`inline-flex items-center gap-1.5 rounded-full border border-brand-darkred/40 text-brand-darkred bg-white hover:bg-brand-darkred hover:text-cream-50 transition-colors text-[12.5px] font-semibold px-3 py-1.5 ${className}`}
      >
        <Download className="h-3.5 w-3.5" strokeWidth={2} />
        {showLabel && <span className="hidden sm:inline">App installieren</span>}
      </button>

      {iosModalOpen && <IosInstallModal onClose={() => setIosModalOpen(false)} />}
    </>
  );
}

/**
 * iOS-Bottom-Toast beim ersten Besuch — zeigt Teilen-Anleitung.
 * Erscheint frühestens 4s nach Page-Load (nicht aggressiv) und nur einmal.
 */
export function IosInstallHint() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isIOSSafari() || isStandalone()) return;
    let seen = false;
    try { seen = localStorage.getItem(STORAGE_KEYS.IOS_HINT_SEEN) === "1"; } catch { /* private */ }
    if (seen) return;
    const t = setTimeout(() => setShow(true), 4000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEYS.IOS_HINT_SEEN, "1"); } catch { /* ignore */ }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-ios-hint"
      className="fixed inset-x-3 bottom-3 z-[80] sm:left-auto sm:right-4 sm:max-w-sm"
    >
      <div className="rounded-2xl bg-brand-darkred text-cream-50 shadow-2xl border border-brand-red/40 p-4">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-cream-50/15">
            <Smartphone className="h-5 w-5" strokeWidth={1.6} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-display text-[15px] leading-tight mb-1">Als App aufs iPhone?</div>
            <div className="text-[12.5px] text-cream-100/85 leading-relaxed">
              Tipp auf <Share className="inline h-3 w-3 -mt-0.5 mx-0.5" /> unten, dann <strong>{'\u201E'}Zum Home-Bildschirm{'\u201C'}</strong>. Fertig.
            </div>
          </div>
          <button
            onClick={dismiss}
            aria-label="Hinweis schließen"
            data-testid="pwa-ios-hint-dismiss"
            className="text-cream-100/70 hover:text-cream-50 p-1 -m-1 shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal für iOS mit ausführlicher Anleitung — via Header-Button geöffnet.
 */
function IosInstallModal({ onClose }) {
  return (
    <div
      data-testid="pwa-ios-modal"
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-4 bg-espresso-deep/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-brand-darkred text-cream-50 p-5 relative">
          <button onClick={onClose} className="absolute top-3 right-3 p-1 text-cream-100/70 hover:text-cream-50" aria-label="Schließen" data-testid="pwa-ios-modal-close">
            <X className="h-4 w-4" />
          </button>
          <div className="inline-flex items-center gap-2 rounded-full bg-cream-50/15 px-2.5 py-1 text-[10.5px] font-bold tracking-widest mb-2">
            <Smartphone className="h-3 w-3" /> iOS · SAFARI
          </div>
          <div className="font-display text-xl leading-tight">Als App auf dein iPhone</div>
          <div className="text-[13px] text-cream-100/85 mt-1">3 Schritte · dauert 15 Sekunden</div>
        </div>
        <ol className="p-5 space-y-3 text-[13.5px] text-sand-800">
          <Step n="1" icon={<Share className="h-4 w-4" />}>
            Tipp unten in Safari auf das <strong>Teilen-Icon</strong> (Quadrat mit Pfeil ↑).
          </Step>
          <Step n="2" icon={<Plus className="h-4 w-4" />}>
            Wähle im Menü <strong>{'\u201E'}Zum Home-Bildschirm{'\u201C'}</strong>. Falls du scrollen musst — es steht weiter unten.
          </Step>
          <Step n="3" icon={<Smartphone className="h-4 w-4" />}>
            Bestätige mit <strong>{'\u201E'}Hinzufügen{'\u201C'}</strong>. Das Dickies-Helden-Icon erscheint sofort auf deinem Homescreen.
          </Step>
        </ol>
        <div className="px-5 pb-5">
          <button onClick={onClose} className="w-full rounded-full bg-brand-darkred text-cream-50 py-2.5 text-[13.5px] font-semibold hover:bg-brand-red">
            Verstanden
          </button>
        </div>
      </div>
    </div>
  );
}

function Step({ n, icon, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cream-100 text-brand-darkred border border-cream-300">
        <span className="text-[13px] font-bold">{n}</span>
      </span>
      <div className="pt-1 leading-relaxed">
        <span className="inline-flex items-center gap-1 text-brand-darkred mr-1 align-middle">{icon}</span>
        {children}
      </div>
    </li>
  );
}
